import { useInfiniteQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { Alert, Button, Card, Field, Input, PageTitle, Spinner, Table } from "@/components/ui";
import { apiFetch, errorMessage } from "@/lib/api";

type Entry = {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  actorName: string | null;
  ip: string | null;
};

const PAGE = 50;

export function AuditPage() {
  const [action, setAction] = useState("");
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const q = useInfiniteQuery({
    queryKey: ["auditoria", filter],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE) });
      if (filter) params.set("action", filter);
      if (pageParam) params.set("before", pageParam);
      return apiFetch<Entry[]>(`/auditoria?${params.toString()}`);
    },
    getNextPageParam: (last) =>
      last.length === PAGE ? (last[last.length - 1]?.createdAt ?? null) : null,
  });

  const rows = q.data?.pages.flat() ?? [];

  return (
    <div className="space-y-6">
      <PageTitle
        title="Auditoria"
        description="Quem fez o quê, quando e de onde. Registros não podem ser alterados."
      />
      <Card>
        <form
          className="mb-4 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setFilter(action.trim());
          }}
        >
          <div className="w-72">
            <Field label="Ação (ex.: membership.roles_changed)">
              <Input value={action} onChange={(e) => setAction(e.target.value)} />
            </Field>
          </div>
          <Button type="submit" variant="secondary">
            Filtrar
          </Button>
        </form>

        {q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <Alert tone="error">{errorMessage(q.error)}</Alert>
        ) : (
          <>
            <Table head={["Quando", "Quem", "Ação", "Registro", ""]}>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr>
                    <td className="whitespace-nowrap px-3 py-2">
                      {new Date(r.createdAt).toLocaleString("pt-BR")}
                    </td>
                    <td className="px-3 py-2">{r.actorName ?? "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {r.action}
                      {r.metadata?.["selfApproved"] === true && (
                        <span className="ml-2 font-sans font-medium">(autoaprovação)</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.entityType}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setOpen(open === r.id ? null : r.id)}
                      >
                        {open === r.id ? "Ocultar" : "Detalhes"}
                      </Button>
                    </td>
                  </tr>
                  {open === r.id && (
                    <tr>
                      <td colSpan={5} className="bg-muted px-3 py-2">
                        <pre className="overflow-x-auto text-xs">
                          {JSON.stringify(
                            { antes: r.before, depois: r.after, detalhes: r.metadata, ip: r.ip },
                            null,
                            2,
                          )}
                        </pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </Table>
            {q.hasNextPage && (
              <Button
                className="mt-4"
                variant="secondary"
                disabled={q.isFetchingNextPage}
                onClick={() => void q.fetchNextPage()}
              >
                Carregar mais
              </Button>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
